/**
 * Offline DataStore Implementation
 *
 * Uses IndexedDB for persistent local storage.
 * No network calls, no Supabase - fully offline.
 */

import type {
  IDataStore,
  IListsStore,
  ISongsStore,
  IPerformancesStore,
  IAuthStore,
  ServiceResult,
  SongFilters,
  SongWithStats,
  Song,
  SongInsert,
  SongUpdate,
  List,
  ListInsert,
  ListUpdate,
  Performance,
  PerformanceInsert,
  PerformanceUpdate,
  OfflineUser,
  AppUser,
  AuthMode,
} from './types';

// =============================================================================
// CONSTANTS
// =============================================================================

const DB_NAME = 'singlist-offline';
const DB_VERSION = 2;
const STORES = {
  LISTS: 'lists',
  SONGS: 'songs',
  PERFORMANCES: 'performances',
} as const;

const USER_STORAGE_KEY = 'singlist-offline-user';

// =============================================================================
// HELPERS
// =============================================================================

function generateUUID(): string {
  // Use crypto.randomUUID if available, otherwise fallback
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function now(): string {
  return new Date().toISOString();
}

// =============================================================================
// INDEXEDDB WRAPPER
// =============================================================================

class IndexedDBWrapper {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  async open(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`));
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = (event.target as IDBOpenDBRequest).transaction;

        // Lists store with indexes
        if (!db.objectStoreNames.contains(STORES.LISTS)) {
          const listsStore = db.createObjectStore(STORES.LISTS, { keyPath: 'id' });
          listsStore.createIndex('user_id', 'user_id', { unique: false });
          listsStore.createIndex('is_default', 'is_default', { unique: false });
        }

        // Songs store with indexes
        if (!db.objectStoreNames.contains(STORES.SONGS)) {
          const songsStore = db.createObjectStore(STORES.SONGS, { keyPath: 'id' });
          songsStore.createIndex('user_id', 'user_id', { unique: false });
          songsStore.createIndex('list_id', 'list_id', { unique: false });
          songsStore.createIndex('created_at', 'created_at', { unique: false });
        } else if (transaction) {
          // Add list_id index if upgrading from version 1
          const songsStore = transaction.objectStore(STORES.SONGS);
          if (!songsStore.indexNames.contains('list_id')) {
            songsStore.createIndex('list_id', 'list_id', { unique: false });
          }
        }

        // Performances store with indexes
        if (!db.objectStoreNames.contains(STORES.PERFORMANCES)) {
          const perfStore = db.createObjectStore(STORES.PERFORMANCES, { keyPath: 'id' });
          perfStore.createIndex('song_id', 'song_id', { unique: false });
          perfStore.createIndex('performed_at', 'performed_at', { unique: false });
        }
      };
    });

    return this.initPromise;
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
    }
  }

  private getStore(storeName: string, mode: IDBTransactionMode): IDBObjectStore {
    if (!this.db) {
      throw new Error('Database not initialized');
    }
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  async getAll<T>(storeName: string, indexName?: string, indexValue?: IDBValidKey): Promise<T[]> {
    await this.open();
    return new Promise((resolve, reject) => {
      const store = this.getStore(storeName, 'readonly');
      let request: IDBRequest<T[]>;

      if (indexName && indexValue !== undefined) {
        const index = store.index(indexName);
        request = index.getAll(indexValue);
      } else {
        request = store.getAll();
      }

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error(`Failed to get all from ${storeName}`));
    });
  }

  async get<T>(storeName: string, key: IDBValidKey): Promise<T | undefined> {
    await this.open();
    return new Promise((resolve, reject) => {
      const store = this.getStore(storeName, 'readonly');
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error(`Failed to get from ${storeName}`));
    });
  }

  async put<T>(storeName: string, value: T): Promise<T> {
    await this.open();
    return new Promise((resolve, reject) => {
      const store = this.getStore(storeName, 'readwrite');
      const request = store.put(value);

      request.onsuccess = () => resolve(value);
      request.onerror = () => reject(new Error(`Failed to put to ${storeName}`));
    });
  }

  async delete(storeName: string, key: IDBValidKey): Promise<void> {
    await this.open();
    return new Promise((resolve, reject) => {
      const store = this.getStore(storeName, 'readwrite');
      const request = store.delete(key);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to delete from ${storeName}`));
    });
  }

  async count(storeName: string, indexName?: string, indexValue?: IDBValidKey): Promise<number> {
    await this.open();
    return new Promise((resolve, reject) => {
      const store = this.getStore(storeName, 'readonly');
      let request: IDBRequest<number>;

      if (indexName && indexValue !== undefined) {
        const index = store.index(indexName);
        request = index.count(indexValue);
      } else {
        request = store.count();
      }

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error(`Failed to count ${storeName}`));
    });
  }
}

// =============================================================================
// OFFLINE LISTS STORE
// =============================================================================

class OfflineListsStore implements IListsStore {
  private db: IndexedDBWrapper;

  constructor(db: IndexedDBWrapper) {
    this.db = db;
  }

  async getAll(userId: string): Promise<ServiceResult<List[]>> {
    try {
      const allLists = await this.db.getAll<List>(STORES.LISTS, 'user_id', userId);
      // Sort by sort_order, then by created_at
      allLists.sort((a, b) => {
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
      return { data: allLists, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch lists' },
      };
    }
  }

  async get(listId: string): Promise<ServiceResult<List>> {
    try {
      const list = await this.db.get<List>(STORES.LISTS, listId);
      if (!list) {
        return { data: null, error: { message: 'List not found', code: 'NOT_FOUND' } };
      }
      return { data: list, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch list' },
      };
    }
  }

  async getDefault(userId: string): Promise<ServiceResult<List>> {
    try {
      const allLists = await this.db.getAll<List>(STORES.LISTS, 'user_id', userId);
      const defaultList = allLists.find((l) => l.is_default);
      if (!defaultList) {
        return { data: null, error: { message: 'Default list not found', code: 'NOT_FOUND' } };
      }
      return { data: defaultList, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch default list' },
      };
    }
  }

  async create(listData: ListInsert): Promise<ServiceResult<List>> {
    try {
      const list: List = {
        ...listData,
        id: listData.id ?? generateUUID(),
        is_public: listData.is_public ?? false,
        share_token: listData.share_token ?? generateUUID(),
        created_at: listData.created_at ?? now(),
        updated_at: listData.updated_at ?? now(),
      };
      await this.db.put(STORES.LISTS, list);
      return { data: list, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create list' },
      };
    }
  }

  async update(listId: string, updates: ListUpdate): Promise<ServiceResult<List>> {
    try {
      const existing = await this.db.get<List>(STORES.LISTS, listId);
      if (!existing) {
        return { data: null, error: { message: 'List not found', code: 'NOT_FOUND' } };
      }
      const updated: List = {
        ...existing,
        ...updates,
        updated_at: now(),
      };
      await this.db.put(STORES.LISTS, updated);
      return { data: updated, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update list' },
      };
    }
  }

  async delete(listId: string): Promise<ServiceResult<{ success: boolean }>> {
    try {
      const list = await this.db.get<List>(STORES.LISTS, listId);
      if (list?.is_default) {
        return {
          data: null,
          error: { message: 'Cannot delete the default list', code: 'CANNOT_DELETE_DEFAULT' },
        };
      }
      await this.db.delete(STORES.LISTS, listId);
      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete list' },
      };
    }
  }

  async count(userId: string): Promise<ServiceResult<number>> {
    try {
      const count = await this.db.count(STORES.LISTS, 'user_id', userId);
      return { data: count, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to count lists' },
      };
    }
  }

  /**
   * Create the default list for a user if it doesn't exist.
   * This is called when initializing the datastore.
   */
  async ensureDefaultList(userId: string): Promise<ServiceResult<List>> {
    try {
      // Check if default list already exists
      const allLists = await this.db.getAll<List>(STORES.LISTS, 'user_id', userId);
      const existingDefault = allLists.find((l) => l.is_default);
      if (existingDefault) {
        return { data: existingDefault, error: null };
      }

      // Create default list
      const defaultList: List = {
        id: generateUUID(),
        user_id: userId,
        name: 'My Songs',
        description: 'Your main karaoke repertoire',
        music_service: null,
        music_service_playlist_id: null,
        music_service_playlist_name: null,
        sort_order: 0,
        is_default: true,
        is_public: false,
        share_token: generateUUID(),
        created_at: now(),
        updated_at: now(),
      };
      await this.db.put(STORES.LISTS, defaultList);
      return { data: defaultList, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create default list' },
      };
    }
  }
}

// =============================================================================
// OFFLINE SONGS STORE
// =============================================================================

class OfflineSongsStore implements ISongsStore {
  private db: IndexedDBWrapper;
  private getPerformances: (songId: string) => Promise<Performance[]>;
  private getAllPerformances: () => Promise<Performance[]>;

  constructor(
    db: IndexedDBWrapper,
    getPerformances: (songId: string) => Promise<Performance[]>,
    getAllPerformances: () => Promise<Performance[]>
  ) {
    this.db = db;
    this.getPerformances = getPerformances;
    this.getAllPerformances = getAllPerformances;
  }

  /**
   * Enrich a single song with stats (used for single song lookups).
   */
  private async enrichWithStats(song: Song): Promise<SongWithStats> {
    const performances = await this.getPerformances(song.id);
    return this.computeStats(song, performances);
  }

  /**
   * Compute stats for a song given its performances.
   * This is a pure function that doesn't fetch data.
   */
  private computeStats(song: Song, performances: Performance[]): SongWithStats {
    const timesPerformed = performances.length;
    // Performances should be sorted by performed_at desc
    const sortedPerfs = [...performances].sort(
      (a, b) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime()
    );
    const lastPerformance = sortedPerfs[0];

    let avgRating: number | null = null;
    if (timesPerformed > 0) {
      const sum = performances.reduce((acc, p) => acc + p.rating, 0);
      avgRating = sum / timesPerformed;
    }

    return {
      ...song,
      times_performed: timesPerformed,
      last_performed_at: lastPerformance?.performed_at ?? null,
      last_rating: lastPerformance?.rating ?? null,
      avg_rating: avgRating,
    };
  }

  async getAll(listId: string, filters?: SongFilters): Promise<ServiceResult<SongWithStats[]>> {
    try {
      const allSongs = await this.db.getAll<Song>(STORES.SONGS, 'list_id', listId);

      // Filter out deleted songs (unless includeDeleted)
      let songs = filters?.includeDeleted
        ? allSongs
        : allSongs.filter((s) => !s.deleted_at);

      // Apply filters
      if (filters?.difficulties?.length) {
        songs = songs.filter((s) => s.difficulty && filters.difficulties!.includes(s.difficulty));
      }
      if (filters?.vocalRangeLabels?.length) {
        songs = songs.filter(
          (s) => s.vocal_range_label && filters.vocalRangeLabels!.includes(s.vocal_range_label)
        );
      }
      if (filters?.isDuet !== undefined) {
        songs = songs.filter((s) => s.is_duet === filters.isDuet);
      }
      if (filters?.genre) {
        songs = songs.filter((s) => s.genre === filters.genre);
      }
      if (filters?.era) {
        const decade = parseInt(filters.era, 10);
        songs = songs.filter(
          (s) => s.release_year && s.release_year >= decade && s.release_year < decade + 10
        );
      }
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        songs = songs.filter(
          (s) =>
            s.title.toLowerCase().includes(query) || s.artist.toLowerCase().includes(query)
        );
      }

      // Batch load all performances at once (fixes N+1 query issue)
      // Instead of fetching performances for each song individually,
      // we load them all and group by song_id
      const allPerformances = await this.getAllPerformances();
      const performancesBySongId = new Map<string, Performance[]>();
      for (const perf of allPerformances) {
        const existing = performancesBySongId.get(perf.song_id) ?? [];
        existing.push(perf);
        performancesBySongId.set(perf.song_id, existing);
      }

      // Enrich with stats using pre-loaded performances (O(n) instead of O(n²))
      const songsWithStats = songs.map((s) => {
        const songPerfs = performancesBySongId.get(s.id) ?? [];
        return this.computeStats(s, songPerfs);
      });

      // Apply performance-based filters after enrichment
      let result = songsWithStats;
      if (filters?.neverSung) {
        result = result.filter((s) => s.times_performed === 0);
      }
      if (filters?.notSungIn30Days) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        result = result.filter(
          (s) => !s.last_performed_at || new Date(s.last_performed_at) < thirtyDaysAgo
        );
      }

      // Sort by created_at descending
      result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      return { data: result, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch songs' },
      };
    }
  }

  async get(songId: string): Promise<ServiceResult<SongWithStats>> {
    try {
      const song = await this.db.get<Song>(STORES.SONGS, songId);
      if (!song) {
        return { data: null, error: { message: 'Song not found', code: 'NOT_FOUND' } };
      }
      const enriched = await this.enrichWithStats(song);
      return { data: enriched, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch song' },
      };
    }
  }

  async create(songData: SongInsert): Promise<ServiceResult<Song>> {
    try {
      const song: Song = {
        ...songData,
        id: songData.id ?? generateUUID(),
        created_at: songData.created_at ?? now(),
        updated_at: songData.updated_at ?? now(),
      };
      await this.db.put(STORES.SONGS, song);
      return { data: song, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create song' },
      };
    }
  }

  async update(songId: string, updates: SongUpdate): Promise<ServiceResult<Song>> {
    try {
      const existing = await this.db.get<Song>(STORES.SONGS, songId);
      if (!existing) {
        return { data: null, error: { message: 'Song not found', code: 'NOT_FOUND' } };
      }
      const updated: Song = {
        ...existing,
        ...updates,
        updated_at: now(),
      };
      await this.db.put(STORES.SONGS, updated);
      return { data: updated, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update song' },
      };
    }
  }

  async delete(songId: string): Promise<ServiceResult<Song>> {
    try {
      const existing = await this.db.get<Song>(STORES.SONGS, songId);
      if (!existing) {
        return { data: null, error: { message: 'Song not found', code: 'NOT_FOUND' } };
      }
      const deleted: Song = {
        ...existing,
        deleted_at: now(),
        updated_at: now(),
      };
      await this.db.put(STORES.SONGS, deleted);
      return { data: deleted, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete song' },
      };
    }
  }

  async restore(songId: string): Promise<ServiceResult<Song>> {
    try {
      const existing = await this.db.get<Song>(STORES.SONGS, songId);
      if (!existing) {
        return { data: null, error: { message: 'Song not found', code: 'NOT_FOUND' } };
      }
      const restored: Song = {
        ...existing,
        deleted_at: null,
        updated_at: now(),
      };
      await this.db.put(STORES.SONGS, restored);
      return { data: restored, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to restore song' },
      };
    }
  }

  async count(listId: string): Promise<ServiceResult<number>> {
    try {
      const allSongs = await this.db.getAll<Song>(STORES.SONGS, 'list_id', listId);
      const activeSongs = allSongs.filter((s) => !s.deleted_at);
      return { data: activeSongs.length, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to count songs' },
      };
    }
  }
}

// =============================================================================
// OFFLINE PERFORMANCES STORE
// =============================================================================

class OfflinePerformancesStore implements IPerformancesStore {
  private db: IndexedDBWrapper;

  constructor(db: IndexedDBWrapper) {
    this.db = db;
  }

  async getAll(songId: string): Promise<ServiceResult<Performance[]>> {
    try {
      const performances = await this.db.getAll<Performance>(
        STORES.PERFORMANCES,
        'song_id',
        songId
      );
      // Sort by performed_at descending
      performances.sort(
        (a, b) => new Date(b.performed_at).getTime() - new Date(a.performed_at).getTime()
      );
      return { data: performances, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to fetch performances' },
      };
    }
  }

  async create(perfData: PerformanceInsert): Promise<ServiceResult<Performance>> {
    try {
      const performance: Performance = {
        ...perfData,
        id: perfData.id ?? generateUUID(),
        created_at: perfData.created_at ?? now(),
        updated_at: perfData.updated_at ?? now(),
      };
      await this.db.put(STORES.PERFORMANCES, performance);
      return { data: performance, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to create performance' },
      };
    }
  }

  async update(
    performanceId: string,
    updates: PerformanceUpdate
  ): Promise<ServiceResult<Performance>> {
    try {
      const existing = await this.db.get<Performance>(STORES.PERFORMANCES, performanceId);
      if (!existing) {
        return { data: null, error: { message: 'Performance not found', code: 'NOT_FOUND' } };
      }
      const updated: Performance = {
        ...existing,
        ...updates,
        updated_at: now(),
      };
      await this.db.put(STORES.PERFORMANCES, updated);
      return { data: updated, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to update performance' },
      };
    }
  }

  async delete(performanceId: string): Promise<ServiceResult<{ success: boolean }>> {
    try {
      await this.db.delete(STORES.PERFORMANCES, performanceId);
      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to delete performance' },
      };
    }
  }
}

// =============================================================================
// OFFLINE AUTH STORE
// =============================================================================

class OfflineAuthStore implements IAuthStore {
  getMode(): AuthMode {
    return 'offline';
  }

  async getOrCreateUser(): Promise<ServiceResult<AppUser>> {
    try {
      // Check localStorage for existing user
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (stored) {
        const user = JSON.parse(stored) as OfflineUser;
        return { data: user, error: null };
      }

      // Create new offline user
      const user: OfflineUser = {
        id: generateUUID(),
        mode: 'offline',
        createdAt: now(),
      };
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      return { data: user, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to get/create user' },
      };
    }
  }

  async getCurrentUser(): Promise<ServiceResult<AppUser | null>> {
    try {
      const stored = localStorage.getItem(USER_STORAGE_KEY);
      if (!stored) {
        return { data: null, error: null };
      }
      const user = JSON.parse(stored) as OfflineUser;
      return { data: user, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to get user' },
      };
    }
  }

  async clear(): Promise<ServiceResult<{ success: boolean }>> {
    try {
      localStorage.removeItem(USER_STORAGE_KEY);
      return { data: { success: true }, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : 'Failed to clear user' },
      };
    }
  }
}

// =============================================================================
// OFFLINE DATASTORE
// =============================================================================

export class OfflineDataStore implements IDataStore {
  private db: IndexedDBWrapper;
  private _lists: OfflineListsStore;
  private _songs: OfflineSongsStore;
  private _performances: OfflinePerformancesStore;
  private _auth: OfflineAuthStore;

  constructor() {
    this.db = new IndexedDBWrapper();
    this._lists = new OfflineListsStore(this.db);
    this._performances = new OfflinePerformancesStore(this.db);
    // Songs store needs access to performances for stats calculation
    this._songs = new OfflineSongsStore(
      this.db,
      // Callback for single song performance lookup
      async (songId) => {
        const result = await this._performances.getAll(songId);
        return result.data ?? [];
      },
      // Callback for batch loading all performances (avoids N+1)
      async () => {
        const allPerfs = await this.db.getAll<Performance>(STORES.PERFORMANCES);
        return allPerfs;
      }
    );
    this._auth = new OfflineAuthStore();
  }

  get lists(): IListsStore {
    return this._lists;
  }

  get songs(): ISongsStore {
    return this._songs;
  }

  get performances(): IPerformancesStore {
    return this._performances;
  }

  get auth(): IAuthStore {
    return this._auth;
  }

  async initialize(): Promise<void> {
    await this.db.open();
  }

  /**
   * Ensure the default list exists for a user.
   * This should be called after user authentication.
   */
  async ensureDefaultListForUser(userId: string): Promise<void> {
    await (this._lists as OfflineListsStore).ensureDefaultList(userId);
  }

  async close(): Promise<void> {
    await this.db.close();
  }
}

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

let instance: OfflineDataStore | null = null;

export function getOfflineDataStore(): OfflineDataStore {
  if (!instance) {
    instance = new OfflineDataStore();
  }
  return instance;
}
